import React, { useState, useEffect } from "react";

import { useHistory } from "react-router-dom";
import { Form, Formik, useFormikContext } from "formik";
import Swal from "sweetalert2";
import * as Yup from "yup";

import { Flex, Text, Button } from "@chakra-ui/react";

import Promotoria from "./Promotoria";
import Caracterizacao from "./Caracterizacao";
import Identificacao from "./Identificacao";
import InformacoesContato from "./InformacoesContato";

import ServicoAcolhimentoService from "../../../../main/services/ServicoAcolhimentoService";
import turnToPascalCase from "../../../utils/turnToPascal";
import { isValidCNPJ } from "../../../utils/isValidCNPJ";

const ServicoAcolhimentoCadastrar = () => {
  const history = useHistory();
  const [existCNPJ, setExistCNPJ] = useState(false);
  const [invalidCNPJ, setInvalidCNPJ] = useState(false);
  const [message, setMessage] = useState("");
  const suportedImageFormats = [
    "image/jpg",
    "image/jpeg",
    "image/tiff",
    "image/png",
    "image/bmp",
  ];

  const [modalidades, setModalidades] = useState({
    options: [],
    isLoading: false,
  });

  const FocusError = () => {
    const { errors, isSubmitting, isValidating } = useFormikContext();

    useEffect(() => {
      if (isSubmitting && !isValidating) {
        let keys = Object.keys(errors);
        if (keys.length > 0) {
          const selector = `[name=${keys[0]}]`;
          const errorElement = document.querySelector(selector);
          if (errorElement) {
            errorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            errorElement.focus({ preventScroll: true });
          }
        }
      }
    }, [errors, isSubmitting, isValidating]);

    return null;
  };

  const FocusErrorData = () => {
    const { values, setFieldError } = useFormikContext();

    useEffect(() => {
      const validateDate = () => {
        const { cnpj } = values;

        if (cnpj) {
          if (isValidCNPJ(cnpj)) {
            setInvalidCNPJ(true);
            setFieldError("cnpj", "");

            ServicoAcolhimentoService.postValidCNPJ(cnpj, 0)
              .then((res) => {
                if (res.data) {
                  setMessage("CNPJ já existente em um Serviço de Acolhimento.");
                  setExistCNPJ(true);
                } else {
                  setMessage("");
                  setExistCNPJ(false);
                }
              })
              .catch((error) => {
                console.log(error);
                setMessage("");
                setExistCNPJ(false);
              });
          } else {
            setFieldError("cnpj", "");
            setInvalidCNPJ(false);
          }
        } else {
          setMessage("");
          setInvalidCNPJ(false);
          setExistCNPJ(false);
        }
      };

      validateDate();
    }, [values, values.dataMovimentacao, setFieldError]);

    return null;
  };

  const onKeyDown = (keyEvent) => {
    if (keyEvent.code === "Enter" || keyEvent.code === "NumpadEnter") {
      keyEvent.preventDefault();
    }
  };

  const backAlert = () => {
    Swal.fire({
      title: "Tem certeza que deseja cancelar?",
      text: "As informações serão perdidas.",
      icon: "error",
      showCancelButton: true,
      confirmButtonColor: "#169499",
      cancelButtonColor: "#169499",
      confirmButtonText: "Sim",
      cancelButtonText: "Não",
    }).then((result) => {
      if (result.isConfirmed) {
        history.goBack();
      }
    });
  };

  const schema = Yup.object().shape({
    promotoriaId: Yup.object().required("Este campo é obrigatório!"),
    varaInfanciaId: Yup.object().required("Este campo é obrigatório!"),
    naturezaServico: Yup.string().required("Este campo é obrigatório!"),
    regime: Yup.string().required("Este campo é obrigatório!"),
    modalidade: Yup.object().required("Este campo é obrigatório!").nullable(),
    logomarca: Yup.mixed()
      .test("imageSize", "O arquivo tem mais de 2MB.", (value) => {
        if (value === null) {
          return true;
        } else {
          return value.size <= 2000000;
        }
      })
      .test("imageType", "Anexar uma extensão válida.", (value) => {
        if (value === null) {
          return true;
        } else {
          return suportedImageFormats.includes(value.type);
        }
      }),
    nomeFantasia: Yup.string()
      .required("Este campo é obrigatório!")
      .trim("Este campo é obrigatório!"),
    site: Yup.string(),
    estadoId: Yup.object().required("Este campo é obrigatório!"),
    municipioId: Yup.object().required("Este campo é obrigatório!"),
    logradouro: Yup.string()
      .required("Este campo é obrigatório!")
      .trim("Este campo é obrigatório!"),
    cnpj: Yup.string()
      .test("cnpjValido", "", (value) => {
        if (!isValidCNPJ(value)) {
          return false;
        } else {
          return true;
        }
      })
      .test("unicidadeCNPJ", "", (value) => {
        if (existCNPJ) {
          return false;
        } else {
          return true;
        }
      }),
    cnpjReferencia: Yup.string().test(
      "cnpjReferenciaValido",
      "Informar um CNPJ válido",
      (value) => !value || isValidCNPJ(value)
    ),
    cep: Yup.string()
      .required("Este campo é obrigatório!")
      .min(9, "Cep inválido!"),
    telefones: Yup.array().of(
      Yup.object().shape({
        telefone: Yup.string()
          .min(14, "Telefone inválido!")
          .max(15, "Telefone inválido!"),
        descricao: Yup.string()
          .trim()
          .when("telefone", {
            is: (tel) => {
              return tel === undefined || tel === "";
            },
            then: Yup.string().test(
              "filledWithouTel",
              "Descrição sem telefone!",
              (value) => {
                return value === undefined || value === "";
              }
            ),
          }),
      })
    ),
    emails: Yup.array().of(
      Yup.object().shape({
        email: Yup.string().trim().email("Email inválido!"),
        descricao: Yup.string()
          .trim()
          .when("email", {
            is: (email) => {
              return email === undefined || email === "";
            },
            then: Yup.string().test(
              "filledWithouEmail",
              "Descrição sem email!",
              (value) => {
                return value === undefined || value === "";
              }
            ),
          }),
      })
    ),
    mantenedoras: Yup.array().of(
      Yup.object().shape({
        nome: Yup.string()
          .nullable()
          .trim("Este campo é obrigatório!")
          .test(
            "hasOneValid",
            "Este campo é obrigatório!",
            (value, context) => value
          ),
      })
    ),
  });

  const handleSubmit = (values, actions) => {
    setTimeout(() => {
      const {
        mantenedoras,
        telefones,
        emails,
        modalidade,
        numeroResidencia,
        estadoId,
        municipioId,
        bairroId,
        logomarca,
        promotoriaId,
        varaInfanciaId,
        nomeFantasia,
        logradouro,
        ...resto
      } = values;

      const valuesToSend = {
        ...resto,
        naturezaServico: (resto.naturezaServico || "")
          .normalize("NFD")
          .replace(/[̀-ͯ]/g, "")
          .toLowerCase(),
        telefones: JSON.stringify(
          telefones
            .filter((obj) => obj.telefone.trim() !== "")
            .map((obj) => {
              if (obj.descricao.trim() !== "") {
                return {
                  ...obj,
                  id: 0,
                  descricao: turnToPascalCase(obj.descricao.trim()),
                };
              } else {
                return { ...obj, id: 0, descricao: obj.descricao.trim() };
              }
            })
        ),
        emails: JSON.stringify(
          emails
            .filter((obj) => obj.email.trim() !== "")
            .map((obj) => {
              if (obj.descricao.trim() !== "") {
                return {
                  ...obj,
                  id: 0,
                  descricao: turnToPascalCase(obj.descricao.trim()),
                };
              } else {
                return {
                  ...obj,
                  id: 0,
                  descricao: obj.descricao.trim(),
                };
              }
            })
        ),
        mantenedora: JSON.stringify(mantenedoras),
        mantenedoras: JSON.stringify(mantenedoras),
        modalidade: modalidade?.value ?? "",
        numeroResidencia:
          Number(numeroResidencia) !== 0 ? Number(numeroResidencia) : "",
        estadoId: estadoId.id,
        municipioId: municipioId ? municipioId.id : "",
        bairroId: bairroId ? bairroId.id : "",
        logomarca: logomarca,
        promotoriaId: promotoriaId.id,
        varaInfanciaId: varaInfanciaId.id,
        nomeFantasia: nomeFantasia.trim(),
        logradouro: logradouro.trim(),
        unidadeMpId: 1,
      };

      ServicoAcolhimentoService.novoServicoAcolhimento(valuesToSend)
        .then((res) => {
          Swal.fire({
            title: "Sucesso!",
            text: "Registro cadastrado com sucesso!",
            icon: "success",
            confirmButtonText: "Continuar",
            confirmButtonColor: "#169499",
          }).then(function () {
            history.push("/servico-acolhimento/" + res.data.id);
          });
          actions.setSubmitting(false);
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.data) {
              if (error.response.data.apierror) {
                if (error.response.data.apierror.subErrors) {
                  error.response.data.apierror.subErrors.forEach((erro) => {
                    actions.setFieldError(erro.field, erro.message);
                  });
                }
              } else {
                actions.setFieldError(
                  error.response.data.field,
                  error.response.data.message
                );
              }
            }
          }
          actions.setSubmitting(false);
        });
    }, 200);
  };

  return (
    <Flex
      flex={1}
      flexDir="column"
      px={{ base: "10px", lg: "32px" }}
      pb="20px"
      alignItems="center"
      overflow="auto"
    >
      <Flex
        pt="4px"
        flexDir="row"
        justifyContent="space-between"
        alignItems="center"
        w="100%"
      >
        <Text
          color="#252733"
          fontWeight={700}
          fontSize="24px"
          lineHeight="29.05px"
        >
          Cadastrar
        </Text>
        <Button
          color="#FFF"
          backgroundColor="#169499"
          fontWeight={700}
          fontSize="15px"
          lineHeight="21.6px"
          _hover={{ bg: "#169499" }}
          onClick={backAlert}
          px="45px"
        >
          Voltar
        </Button>
      </Flex>
      <Flex flexDir="column" w="100%">
        <Formik
          validationSchema={schema}
          initialValues={{
            promotoriaId: "",
            varaInfanciaId: "",
            numeroInclusao: "",
            naturezaServico: "",
            regime: "",
            modalidade: "",
            logomarca: null,
            nomeFantasia: "",
            razaoSocial: "",
            cnpj: "",
            cnpjReferencia: "",
            site: "",
            estadoId: "",
            municipioId: "",
            bairroId: "",
            logradouro: "",
            numeroResidencia: "",
            cep: "",
            complemento: "",
            pontoReferencia: "",
            mantenedoras: [
              {
                id: 0,
                nome: "",
              },
            ],
            telefones: [
              {
                telefone: "",
                descricao: "",
              },
            ],
            emails: [
              {
                email: "",
                descricao: "",
              },
            ],
          }}
          onSubmit={handleSubmit}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {(props) => (
            <Form onKeyDown={onKeyDown}>
              <FocusError />
              <FocusErrorData />
              <Promotoria formProps={props} />
              <Caracterizacao
                formProps={props}
                modalidades={modalidades}
                setModalidades={setModalidades}
              />
              <Identificacao
                formProps={props}
                invalidCNPJ={invalidCNPJ}
                existCNPJ={existCNPJ}
                message={message}
              />
              <InformacoesContato formProps={props} />
              <Flex justifyContent="flex-end">
                <Button
                  id="button-salvar-serv"
                  type="submit"
                  translate="no"
                  mt="30px"
                  px="45px"
                  color="#FFF"
                  backgroundColor="#169499"
                  fontWeight={700}
                  fontSize="15px"
                  lineHeight="21.6px"
                  _hover={{ bg: "#169499" }}
                  isLoading={props.isSubmitting}
                >
                  Salvar
                </Button>
              </Flex>
            </Form>
          )}
        </Formik>
      </Flex>
    </Flex>
  );
};

export default ServicoAcolhimentoCadastrar;
